import { useState, useEffect, useRef } from 'react';
import { getPresentations, addPresentation, deletePresentation, getCurriculum, getLesson } from '../utils/api';
import ScriptDrawer from '../components/ScriptDrawer';
import { buildDeckScripts } from '../utils/scriptMap';
import { useAuth } from '../context/AuthContext';
import PresentationPlayer from '../components/PresentationPlayer';
import toast from 'react-hot-toast';

/* ── What CPR sells. One tile per service on the launcher. ──
   `art` is a photo pulled from that service's own deck, so the tile
   looks like the thing it opens. Falls back to the gradient if absent. */
const SERVICES = [
  { name: 'Roofing', tint: '#B4642A',
    art: '/presentations/cpr-roofing-vibrant/assets/img/s07.jpg',
    blurb: 'Tear-off, decking, Owens Corning system' },
  { name: 'Coating', tint: '#1B7A5A',
    art: '/presentations/cpr-coating/slides/slide-1.jpg',
    blurb: 'COOLWALL® exterior coating system' },
  { name: 'Windows & Doors', tint: '#3E6B8A',
    art: '/presentations/cpr-windows-vibrant/assets/img/inst9.jpg',
    blurb: 'Custom fusion-welded vinyl windows' },
  { name: 'HVAC & Insulation', tint: '#7A63A8', art: null, blurb: 'Heating, cooling and attic insulation' },
];

/* Decks shipped with the app. `service` decides which tile they live under. */
const BUILT_IN = [
  { id: 'cpr-roofing-vibrant', service: 'Roofing', title: 'Roofing Presentation',
    meta: '39 slides · animated', type: 'iframe', url: '/presentations/cpr-roofing-vibrant/index.html', star: true },
  { id: 'cpr-coating-vibrant', service: 'Coating', title: 'Coating Presentation',
    meta: '34 slides · animated · videos', type: 'iframe', url: '/presentations/cpr-coating-vibrant/index.html', star: true },
  { id: 'cpr-windows-vibrant', service: 'Windows & Doors', title: 'Windows Presentation',
    meta: '33 slides · animated', type: 'iframe', url: '/presentations/cpr-windows-vibrant/index.html', star: true },
];

function canvaEmbed(url) {
  if (!url) return null;
  const m = url.match(/canva\.com\/design\/([^/]+)\/([^/?]+)/);
  return m ? `https://www.canva.com/design/${m[1]}/${m[2]}/view?embed` : null;
}
const isValidCanva = (u) => /canva\.com\/design\//.test(u) || /\.canva\.site/.test(u);

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Montserrat:wght@500;600;700;800&display=swap');
.pz{--ink:#0F241D;--sub:#67766E;--paper:#fff;--edge:#E3E9E5;--green:#0D3B2E;--gold:#C9922A;
  font-family:Montserrat,system-ui,sans-serif;color:var(--ink);padding-bottom:44px}
.pz-head{margin-bottom:24px}
.pz-kick{font-size:10.5px;font-weight:800;letter-spacing:.2em;text-transform:uppercase;color:var(--gold);margin-bottom:7px}
.pz-practice-note{margin-top:10px;background:#FFF8E6;border:1px solid #F0D99A;border-radius:12px;
  padding:12px 16px;font-size:13.5px;color:#6B4E10;line-height:1.55;max-width:62ch}
.pz-practice-note b{color:#8A3A1A}
.pz-h1{font-family:Fraunces,Georgia,serif;font-size:clamp(28px,4vw,40px);font-weight:700;margin:0;letter-spacing:-.018em;line-height:1.03}
.pz-lede{font-size:14px;color:var(--sub);margin-top:9px}

/* ── service tiles ── */
.pz-tiles{display:grid;grid-template-columns:repeat(auto-fit,minmax(258px,1fr));gap:16px}
.pz-tile{position:relative;border:none;padding:0;cursor:pointer;font:inherit;text-align:left;color:#fff;
  border-radius:22px;overflow:hidden;aspect-ratio:5/3.4;background:var(--tint);
  box-shadow:0 2px 6px rgba(15,36,29,.10);
  transition:transform .3s cubic-bezier(.2,.8,.3,1),box-shadow .3s;
  animation:pzIn .5s cubic-bezier(.2,.8,.3,1) both;animation-delay:calc(var(--i)*80ms)}
.pz-tile:hover{transform:translateY(-4px) scale(1.012);box-shadow:0 20px 40px -18px rgba(15,36,29,.5)}
.pz-tile:active{transform:translateY(-1px) scale(.998)}
.pz-tile:focus-visible{outline:3px solid var(--gold);outline-offset:3px}
.pz-art{position:absolute;inset:0;background-size:cover;background-position:center;
  transition:transform .6s cubic-bezier(.2,.8,.3,1)}
.pz-tile:hover .pz-art{transform:scale(1.06)}
.pz-scrim{position:absolute;inset:0;
  background:linear-gradient(180deg,rgba(9,26,20,.18) 0%,rgba(9,26,20,.62) 52%,rgba(9,26,20,.9) 100%)}
.pz-body{position:absolute;left:0;right:0;bottom:0;padding:20px 22px}
.pz-name{font-family:Fraunces,Georgia,serif;font-size:26px;font-weight:700;letter-spacing:-.015em;line-height:1.05}
.pz-blurb{font-size:12.5px;opacity:.86;margin-top:5px;font-weight:500}
.pz-cta{margin-top:13px;display:inline-flex;align-items:center;gap:8px;background:rgba(255,255,255,.16);
  backdrop-filter:blur(6px);border:1px solid rgba(255,255,255,.28);border-radius:999px;padding:9px 17px;
  font-size:13px;font-weight:700;transition:background .22s,gap .22s}
.pz-tile:hover .pz-cta{background:var(--gold);border-color:var(--gold);color:#20321A;gap:12px}
.pz-cnt{position:absolute;top:15px;right:16px;background:rgba(9,26,20,.55);backdrop-filter:blur(5px);
  border-radius:999px;padding:5px 12px;font-size:11.5px;font-weight:700}
.pz-soon{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;
  background:rgba(9,26,20,.55);font-size:12.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase}

/* ── picker sheet ── */
.pz-sheet{position:fixed;inset:0;background:rgba(9,20,16,.6);backdrop-filter:blur(4px);z-index:80;
  display:flex;align-items:center;justify-content:center;padding:20px;animation:pzFade .2s both}
.pz-panel{background:var(--paper);border-radius:24px;max-width:560px;width:100%;padding:26px;
  animation:pzPop .32s cubic-bezier(.2,.9,.3,1) both}
.pz-panel h2{font-family:Fraunces,Georgia,serif;font-size:23px;margin:0 0 3px;font-weight:600}
.pz-panel .s{font-size:12.5px;color:var(--sub);margin-bottom:18px}
.pz-opt{display:flex;align-items:center;gap:14px;width:100%;text-align:left;font:inherit;cursor:pointer;
  background:#FAFCFB;border:1px solid var(--edge);border-radius:16px;padding:16px 18px;margin-bottom:9px;
  transition:border-color .2s,background .2s,transform .2s;text-decoration:none;color:inherit}
.pz-opt:hover{background:#fff;border-color:var(--green);transform:translateX(3px)}
.pz-opt:focus-visible{outline:2px solid var(--gold);outline-offset:2px}
.pz-opt .t{flex:1;min-width:0}
.pz-opt .n{font-size:15px;font-weight:700}
.pz-opt .m{font-size:12px;color:var(--sub);margin-top:2px}
.pz-opt .go{font-size:19px;color:var(--green)}
.pz-star{font-size:9px;font-weight:800;letter-spacing:.08em;background:linear-gradient(135deg,#E0B24A,#C9922A);
  color:#20321A;padding:3px 8px;border-radius:999px;margin-left:8px}
.pz-close{margin-top:8px;width:100%;font:inherit;font-size:13.5px;font-weight:700;background:none;border:none;
  color:var(--sub);padding:12px;cursor:pointer;border-radius:12px}
.pz-close:hover{background:#F2F5F3;color:var(--ink)}

/* ── admin drawer ── */
.pz-manage{margin-top:26px;text-align:center}
.pz-mbtn{font:inherit;font-size:12.5px;font-weight:700;color:var(--sub);background:none;border:none;cursor:pointer;
  padding:9px 16px;border-radius:999px;transition:background .18s,color .18s}
.pz-mbtn:hover{background:#E6EDE9;color:var(--ink)}
.pz-admin{margin-top:14px;background:var(--paper);border:1px solid var(--edge);border-radius:20px;padding:22px;
  animation:pzIn .3s both;text-align:left}
.pz-admin h3{font-family:Fraunces,Georgia,serif;font-size:17px;margin:0 0 3px;font-weight:600}
.pz-admin p{margin:0 0 15px;font-size:12.5px;color:var(--sub)}
.pz-f{display:grid;grid-template-columns:2fr 1fr 1fr;gap:10px;margin-bottom:10px}
.pz-in{font:inherit;font-size:13.5px;padding:12px 14px;border:1px solid var(--edge);border-radius:12px;
  background:#FAFCFB;width:100%;box-sizing:border-box;transition:border-color .2s,box-shadow .2s}
.pz-in:focus{outline:none;background:#fff;border-color:var(--green);box-shadow:0 0 0 4px rgba(13,59,46,.08)}
.pz-add{font:inherit;font-size:13.5px;font-weight:700;border:none;border-radius:999px;padding:12px 24px;cursor:pointer;
  background:linear-gradient(135deg,#14503C,#0D3B2E);color:#fff;box-shadow:0 2px 10px -3px rgba(13,59,46,.5)}
.pz-add:hover{filter:brightness(1.09)}
.pz-existing{margin-top:16px;border-top:1px solid var(--edge);padding-top:14px}
.pz-erow{display:flex;align-items:center;gap:10px;font-size:13px;padding:8px 0;border-bottom:1px solid #F2F5F3}
.pz-erow .sv{font-size:11px;font-weight:700;color:var(--sub);background:#EEF3F0;padding:3px 9px;border-radius:999px}
.pz-erow button{margin-left:auto;border:none;background:none;color:#B4453A;cursor:pointer;font-size:14px;padding:4px 8px;border-radius:6px}
.pz-erow button:hover{background:#FBEBE9}

@keyframes pzIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
@keyframes pzFade{from{opacity:0}to{opacity:1}}
@keyframes pzPop{from{opacity:0;transform:translateY(16px) scale(.98)}to{opacity:1;transform:none}}
@media(prefers-reduced-motion:reduce){.pz-tile,.pz-panel,.pz-sheet,.pz-admin{animation:none}
  .pz-tile,.pz-art,.pz-opt{transition:none}}
@media(max-width:640px){.pz-tiles{grid-template-columns:1fr;gap:13px}.pz-tile{aspect-ratio:16/9}
  .pz-name{font-size:23px}.pz-f{grid-template-columns:1fr}}
`;

/* The script drawer an admin sees over a presentation while teaching.
   Set to false to switch it off without touching anything else. */
const SCRIPT_DRAWER_ENABLED = true;

/* The animated decks carry scripts. */
const deckKeyFor = (url) => /coating-vibrant/.test(url || '') ? 'coat'
                          : /roofing-vibrant/.test(url || '') ? 'roof'
                          : /windows-vibrant/.test(url || '') ? 'win' : null;

export default function Presentations({ withScript = false }) {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: '', url: '', service: 'Roofing', kind: 'Sales deck' });
  const [pick, setPick] = useState(null);      // service tile that was tapped
  const [manage, setManage] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [deck, setDeck] = useState(null);
  const [slide, setSlide] = useState(1);
  const [scripts, setScripts] = useState({});        // { coat: {...}, roof: {...} }
  const frameRef = useRef(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    function onMsg(e) {
      if (e.data === 'close-vibrant') setDeck(null);
      // the deck announces each slide change so the script can follow it
      if (e.data && typeof e.data.deckSlide === 'number') setSlide(e.data.deckSlide);
    }
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  /* Load the scripts the first time an admin opens a scripted deck.
     Only the Practice Room loads scripts; Presentations never does. */
  const deckKey = deckKeyFor(deck);
  const drawerOn = SCRIPT_DRAWER_ENABLED && withScript && !!deckKey;
  useEffect(() => {
    if (!drawerOn || scripts[deckKey]) return;
    let cancelled = false;
    (async () => {
      try {
        const cur = await getCurriculum();
        const levels = (cur.levels || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        const refs = [];
        levels.forEach((lv, i) => (lv.lessons || []).forEach(l =>
          refs.push({ id: l.id, stepTitle: lv.title, order: i })));
        const loaded = await Promise.all(refs.map(r =>
          getLesson(r.id).then(d => ({ ...r, title: (d.lesson && d.lesson.title) || r.stepTitle,
                                           content: (d.lesson && d.lesson.content) || '' }))
                         .catch(() => null)));
        if (cancelled) return;
        setScripts(prev => ({ ...prev, [deckKey]: buildDeckScripts(deckKey, loaded.filter(Boolean)) }));
      } catch (e) { /* no script is better than a broken deck */ }
    })();
    return () => { cancelled = true; };
  }, [drawerOn, deckKey, scripts]);

  // a fresh deck always starts at slide 1
  useEffect(() => { if (deck) setSlide(1); }, [deck]);

  useEffect(() => { load(); }, []);
  useEffect(() => {
    function esc(e) { if (e.key === 'Escape') setPick(null); }
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, []);

  async function load() {
    setLoading(true);
    try { const d = await getPresentations(); setItems(d.presentations || []); }
    catch { toast.error('Could not load presentations'); }
    finally { setLoading(false); }
  }
  async function add() {
    if (!form.title || !form.url) return toast.error('Add a title and the Canva link');
    if (!isValidCanva(form.url)) return toast.error('That link isn\u2019t a Canva design or canva.site page');
    try { await addPresentation(form); toast.success('Added'); setForm(f => ({ ...f, title: '', url: '' })); load(); }
    catch { toast.error('Could not add it'); }
  }
  async function remove(p) {
    if (!window.confirm(`Remove "${p.title}"?`)) return;
    try { await deletePresentation(p.id); toast.success('Removed'); load(); } catch { toast.error('Failed'); }
  }

  // everything openable, grouped by service
  // Roofing and Coating are presented with the animated decks only — any Canva
  // link filed under those services is hidden from reps (still editable in Manage).
  const ANIMATED_ONLY = ['Roofing', 'Coating'];
  const all = [
    ...BUILT_IN,
    ...items
      .map(p => ({ id: 'c' + p.id, service: p.service || 'Other', title: p.title,
        meta: canvaEmbed(p.url) ? 'Canva presentation' : 'Canva link', type: 'link', url: p.url, raw: p }))
      .filter(p => !ANIMATED_ONLY.includes(p.service)),
  ];
  const forService = (s) => all.filter(x => x.service === s);

  function open(entry) {
    setPick(null);
    if (entry.type === 'iframe') setDeck(entry.url);
    else if (entry.type === 'native') setPlaying(entry.baseUrl);
    else window.open(entry.url, '_blank', 'noopener');
  }
  function tapService(sv) {
    const list = forService(sv.name);
    if (list.length === 1) return open(list[0]);   // one deck — go straight in
    if (list.length) setPick(sv);
  }

  return (
    <>
      <style>{CSS}</style>
      {playing && <PresentationPlayer baseUrl={playing} onClose={() => setPlaying(null)} />}
      {deck && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#000' }}>
          <iframe ref={frameRef} title="Presentation" src={deck} allow="autoplay; fullscreen"
            style={{ width: drawerOpen ? 'calc(100% - min(440px, 46vw))' : '100%', height: '100%', border: 0,
                     transition: 'width .26s cubic-bezier(.2,.8,.3,1)' }} />
          {drawerOn && scripts[deckKey] && (
            <ScriptDrawer data={scripts[deckKey]} slide={slide} onOpenChange={setDrawerOpen} />
          )}
          <button onClick={() => setDeck(null)} aria-label="Close presentation"
            style={{ position: 'absolute', top: 14, right: drawerOpen ? 'calc(min(440px, 46vw) + 18px)' : 18, width: 42, height: 42, borderRadius: '50%', border: 0, background: 'rgba(255,255,255,.16)', color: '#fff', fontSize: 18, cursor: 'pointer', transition: 'right .26s cubic-bezier(.2,.8,.3,1)' }}>✕</button>
        </div>
      )}

      <div className="page-body pz">
        <div className="pz-head">
          {withScript ? (<>
            <div className="pz-kick" style={{ color: '#C9922A' }}>Practice Room · the script is shown</div>
            <h1 className="pz-h1">What are you practising today?</h1>
            <div className="pz-practice-note">
              For studying and teaching. Open any deck and the script for each slide is one tap away.
              <b> Never use this in front of a customer</b> — for that, use Presentations.
            </div>
          </>) : (<>
            <div className="pz-kick">Sales presentations</div>
            <h1 className="pz-h1">What are you presenting today?</h1>
          </>)}
          <div className="pz-lede">Pick a service. It opens full screen, ready to show.</div>
        </div>

        <div className="pz-tiles">
          {SERVICES.map((sv, i) => {
            const list = forService(sv.name);
            const ready = list.length > 0;
            return (
              <button key={sv.name} className="pz-tile" style={{ '--i': i, '--tint': sv.tint }}
                onClick={() => tapService(sv)} disabled={!ready}
                aria-label={ready ? `Present ${sv.name}` : `${sv.name} — coming soon`}>
                {sv.art && <div className="pz-art" style={{ backgroundImage: `url('${sv.art}')` }} />}
                <div className="pz-scrim" />
                {ready && list.length > 1 && <div className="pz-cnt">{list.length} decks</div>}
                <div className="pz-body">
                  <div className="pz-name">{sv.name}</div>
                  <div className="pz-blurb">{sv.blurb}</div>
                  {ready && <span className="pz-cta">Present <span aria-hidden>→</span></span>}
                </div>
                {!ready && <div className="pz-soon">Coming soon</div>}
              </button>
            );
          })}
        </div>

        {pick && (
          <div className="pz-sheet" onClick={e => { if (e.currentTarget === e.target) setPick(null); }}>
            <div className="pz-panel" role="dialog" aria-modal="true" aria-label={`${pick.name} presentations`}>
              <h2>{pick.name}</h2>
              <div className="s">Choose what to show.</div>
              {forService(pick.name).map(x => (
                <button key={x.id} className="pz-opt" onClick={() => open(x)}>
                  <div className="t">
                    <div className="n">{x.title}{x.star && <span className="pz-star">ANIMATED</span>}</div>
                    <div className="m">{x.meta}</div>
                  </div>
                  <span className="go" aria-hidden>→</span>
                </button>
              ))}
              <button className="pz-close" onClick={() => setPick(null)}>Cancel</button>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="pz-manage">
            <button className="pz-mbtn" onClick={() => setManage(m => !m)}>
              {manage ? 'Done managing' : '⚙ Manage presentations'}
            </button>
            {manage && (
              <div className="pz-admin">
                <h3>Add a Canva presentation</h3>
                <p>It appears under the service you choose.</p>
                <div className="pz-f">
                  <input className="pz-in" placeholder="Title — e.g. Roofing before &amp; after"
                    value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                  <select className="pz-in" value={form.service} onChange={e => setForm(f => ({ ...f, service: e.target.value }))}>
                    {SERVICES.map(s => <option key={s.name}>{s.name}</option>)}
                  </select>
                  <select className="pz-in" value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))}>
                    {['Sales deck', 'Before & after', 'Video', 'Letters & docs'].map(k => <option key={k}>{k}</option>)}
                  </select>
                </div>
                <input className="pz-in" style={{ marginBottom: 13 }} placeholder="Canva link"
                  value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
                <button className="pz-add" onClick={add}>Add presentation</button>
                {items.length > 0 && (
                  <div className="pz-existing">
                    {items.map(p => (
                      <div className="pz-erow" key={p.id}>
                        <span className="sv">{p.service || 'Roofing'}</span>
                        <span>{p.title}</span>
                        <button onClick={() => remove(p)} aria-label={`Remove ${p.title}`}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {loading && <div style={{ padding: 20, color: '#9aa5a0', fontSize: 13 }}>Loading…</div>}
      </div>
    </>
  );
}
